<template>
  <div>
    <label style="display: block; font-size: 0.875rem; font-weight: 500; color: #374151; margin-bottom: 0.125rem;">Contato para cobrança</label>
    <p style="font-size: 0.8125rem; color: #6b7280; margin: 0 0 0.625rem 0;">Para envio de cobranças, notas fiscais e contato em caso de pendências.</p>
    <div class="invite-contact-grid">
      <div>
        <label class="invite-field-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-field-icon">
            <rect width="20" height="16" x="2" y="4" rx="2"></rect>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
          </svg>
          E-mail
        </label>
        <input
          v-model="financeEmails"
          type="text"
          class="invite-input"
          :class="{ 'invite-input-error': !!emailError }"
          placeholder="financeiro@suaempresa.com.br">
        <p v-if="emailError" class="invite-field-error">{{ emailError }}</p>
        <p v-else class="invite-field-hint">Até 5 e-mails separados por vírgula.</p>
      </div>

      <div>
        <label class="invite-field-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-field-icon">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"></path>
          </svg>
          Telefone
        </label>
        <input
          v-model="contactPhone"
          type="tel"
          class="invite-input"
          :class="{ 'invite-input-error': (contactPhone && !isValid) || !!phoneError }"
          @keydown="allowOnlyNumericKeydown($event)"
          @input="onPhoneInput"
          placeholder="(11) 99999-9999">
        <p v-if="phoneError" class="invite-field-error">{{ phoneError }}</p>
        <p v-else-if="contactPhone && !isValid" class="invite-field-error">Insira um telefone válido</p>
      </div>
    </div>
  </div>
</template>

<script>
import loopback from '@/services/loopback'
import { formatPhoneMask, phoneDigitsOnly, allowOnlyNumericKeydown, isValidPhone, maskNationalPhone } from '@/utils/phone'
import { triggerUserDataUpdateWebhook } from '@/services/user-data-update-webhook'

// Contato financeiro da organization (mesmas regras da autocompra/recarga):
// pré-preenche dos dados financeiros + telefone do usuário e persiste ao confirmar.
export default {
  name: 'BillingContactFields',
  data () {
    return {
      financeEmails: '',
      contactPhone: '',
      emailError: null,
      phoneError: null
    }
  },
  computed: {
    financialData () {
      return this.$store.state.auth.financialData
    },
    isValid () {
      return isValidPhone(this.contactPhone)
    }
  },
  watch: {
    isValid (value) {
      this.$emit('valid', value)
    }
  },
  mounted () {
    this.financeEmails = (this.financialData && this.financialData.additionalEmails != null) ? this.financialData.additionalEmails : ''
    const user = this.$store.state.auth.user
    this.contactPhone = maskNationalPhone(user && user.phone)
    this.$emit('valid', this.isValid)
  },
  methods: {
    allowOnlyNumericKeydown,
    onPhoneInput () {
      this.contactPhone = formatPhoneMask(phoneDigitsOnly(this.contactPhone))
      this.phoneError = null
    },
    // Persiste e-mails financeiros + telefone (se mudaram). Retorna true em sucesso;
    // false (e exibe o erro) caso a validação ou a persistência falhe.
    async persist () {
      this.emailError = null
      this.phoneError = null
      if (!this.isValid) {
        this.phoneError = 'Insira um telefone válido'
        return false
      }
      try {
        await this.persistFinanceEmailsIfDirty()
      } catch (err) {
        const message = typeof err.message === 'string' ? err.message : ''
        if (message.includes('additionalEmails: invalid')) {
          this.emailError = message.includes('maximum 5 emails')
            ? 'Máximo de 5 emails adicionais.'
            : 'Emails adicionais inválidos. Exemplo: "contato@dominio.com,financeiro@dominio.com"'
        } else {
          // TEL-2243: `err.message` e texto do backend e nao vai para a tela — num 5xx
          // do PUT /accounts/financial isso renderizava "Internal Server Error" embaixo
          // do campo de e-mail. O `if` acima segue lendo a `message` de proposito: ela
          // nunca e sobrescrita, e e o que distingue os dois erros de additionalEmails.
          this.emailError = 'Não foi possível atualizar os e-mails. Tente novamente em alguns minutos.'
        }
        return false
      }
      try {
        await this.persistContactPhoneIfChanged()
      } catch (err) {
        this.phoneError = 'Não foi possível atualizar o telefone.'
        return false
      }
      return true
    },
    async persistFinanceEmailsIfDirty () {
      const currentEmails = this.financeEmails != null ? String(this.financeEmails).trim() : ''
      const storedEmails = this.financialData && this.financialData.additionalEmails != null
        ? String(this.financialData.additionalEmails).trim()
        : ''
      const isDirty = this.financialData && currentEmails !== storedEmails
      if (!isDirty) return
      const updated = await loopback.put('/accounts/financial', { additionalEmails: this.financeEmails })
      if (updated) this.$store.commit('auth/setFinancialData', updated)
    },
    async persistContactPhoneIfChanged () {
      const digits = phoneDigitsOnly(this.contactPhone)
      const currentUser = this.$store.state.auth.user
      const previousPhone = phoneDigitsOnly(currentUser && currentUser.phone)
      if (digits === previousPhone) return
      // Envia o email do usuário LOGADO para o PATCH /users resolver via isSelfUpdate
      // e atualizar o próprio usuário. Sem email, o backend cai no 1º usuário da org
      // (owner/founder) e o setUser abaixo trocaria a sessão do admin para o founder.
      const user = await loopback.patch('/users', {
        email: currentUser && currentUser.email,
        phone: digits
      })
      this.$store.commit('auth/setUser', user)
      triggerUserDataUpdateWebhook({
        email: user.email,
        name: user.name,
        phone: user.phone,
        domain: this.$store.state.auth.organization && this.$store.state.auth.organization.domain
      })
    }
  }
}
</script>

<style scoped>
.invite-contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.invite-field-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin: 0 0 0.375rem 0;
}

.invite-field-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.invite-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #111827;
  background: #ffffff;
  box-sizing: border-box;
}

.invite-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.invite-input-error {
  border-color: #dc2626;
}

.invite-input-error:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}

.invite-field-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0.375rem 0 0 0;
}

.invite-field-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0.375rem 0 0 0;
}

@media (max-width: 640px) {
  .invite-contact-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Users/invite/BillingContactFields.vue